import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  getAnnouncements,
  createAnnouncement,
  getBarangays,
} from '../../lib/api';
import {
  Megaphone,
  Plus,
  Calendar as CalendarIcon,
  Pin,
  Globe,
  MapPin,
} from 'lucide-react';

export default function AnnouncementManagement() {
  const { user, isMunicipalAdmin } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [barangays, setBarangays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [formData, setFormData] = useState({
    barangay_id: '',
    title: '',
    body: '',
    start_date: new Date(),
    end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
    pinned: false,
  });
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [announcementsRes, barangaysRes] = await Promise.all([
        getAnnouncements(),
        getBarangays(),
      ]);
      setAnnouncements(announcementsRes.data);
      setBarangays(barangaysRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      await createAnnouncement({
        ...formData,
        barangay_id: formData.barangay_id || null,
        start_date: formData.start_date.toISOString(),
        end_date: formData.end_date.toISOString(),
      });
      toast.success('Announcement created successfully');
      setDialogOpen(false);
      loadData();
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create announcement');
    } finally {
      setFormLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      barangay_id: '',
      title: '',
      body: '',
      start_date: new Date(),
      end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      pinned: false,
    });
  };

  const getBarangayName = (id) => barangays.find(b => b.id === id)?.name;

  const isActive = (announcement) => {
    const now = new Date();
    return new Date(announcement.start_date) <= now && new Date(announcement.end_date) >= now;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="announcement-management">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-['Public_Sans'] text-foreground">
              Announcements
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage public announcements for residents
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-announcement-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Announcement
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Create Announcement</DialogTitle>
                <DialogDescription>
                  Create a new public announcement
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Scope</Label>
                  <Select
                    value={formData.barangay_id || '__municipal__'}
                    onValueChange={(v) => setFormData({ ...formData, barangay_id: v === '__municipal__' ? '' : v })}
                  >
                    <SelectTrigger data-testid="scope-select">
                      <SelectValue placeholder="Select scope" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__municipal__">
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4" />
                          Municipal-wide
                        </div>
                      </SelectItem>
                      {barangays.map((b) => (
                        <SelectItem key={b.id} value={b.id}>
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4" />
                            {b.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Announcement title"
                    required
                    data-testid="announcement-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Content *</Label>
                  <Textarea
                    value={formData.body}
                    onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                    placeholder="Announcement content..."
                    required
                    rows={4}
                    data-testid="announcement-body-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(formData.start_date, 'PP')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={formData.start_date}
                          onSelect={(date) => setFormData({ ...formData, start_date: date || new Date() })}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(formData.end_date, 'PP')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={formData.end_date}
                          onSelect={(date) => setFormData({ ...formData, end_date: date || new Date() })}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-sm">
                  <div className="flex items-center gap-2">
                    <Pin className="h-4 w-4 text-muted-foreground" />
                    <Label htmlFor="pinned" className="cursor-pointer">Pin to top</Label>
                  </div>
                  <Switch
                    id="pinned"
                    checked={formData.pinned}
                    onCheckedChange={(checked) => setFormData({ ...formData, pinned: checked })}
                    data-testid="pinned-switch"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={formLoading} data-testid="submit-announcement-btn">
                    {formLoading ? 'Creating...' : 'Create'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Announcements List */}
        <div className="grid gap-4">
          {announcements.map((announcement) => (
            <Card 
              key={announcement.id} 
              className={`${announcement.pinned ? 'border-primary/50' : ''} ${!isActive(announcement) ? 'opacity-60' : ''}`}
              data-testid={`announcement-${announcement.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    {announcement.pinned && (
                      <Pin className="h-4 w-4 text-primary" />
                    )}
                    <CardTitle className="text-lg">{announcement.title}</CardTitle>
                  </div>
                  <div className="flex items-center gap-2">
                    {announcement.barangay_id ? (
                      <Badge variant="outline" className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {getBarangayName(announcement.barangay_id)}
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="flex items-center gap-1">
                        <Globe className="h-3 w-3" />
                        Municipal
                      </Badge>
                    )}
                    <Badge variant={isActive(announcement) ? 'default' : 'secondary'}>
                      {isActive(announcement) ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
                <CardDescription>
                  {announcement?.start_date && !isNaN(new Date(announcement.start_date).getTime())
  ? format(new Date(announcement.start_date), 'PP')
  : '-'} - {announcement?.end_date && !isNaN(new Date(announcement.end_date).getTime())
  ? format(new Date(announcement.end_date), 'PP')
  : '-'}

                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{announcement.body}</p>
              </CardContent>
            </Card>
          ))}
          {announcements.length === 0 && (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                <Megaphone className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No announcements yet</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
